// Maps a zodiac sign to its classical element (Fire, Earth, Air, Water) and
// the visual tokens used to render an element badge/card on the dashboard.
// The sign -> element mapping mirrors the backend ElementBalanceAnalyzer so the
// frontend derivation stays consistent with server-side astrology logic.
import { Zodiac } from '../api';
import { Droplet, Flame, LucideIcon, Mountain, Wind } from 'lucide-react';

export type ZodiacElement = 'fire' | 'earth' | 'air' | 'water';

const SIGN_TO_ELEMENT: Record<Zodiac, ZodiacElement> = {
  [Zodiac.Aries]: 'fire',
  [Zodiac.Leo]: 'fire',
  [Zodiac.Sagittarius]: 'fire',
  [Zodiac.Taurus]: 'earth',
  [Zodiac.Virgo]: 'earth',
  [Zodiac.Capricorn]: 'earth',
  [Zodiac.Gemini]: 'air',
  [Zodiac.Libra]: 'air',
  [Zodiac.Aquarius]: 'air',
  [Zodiac.Cancer]: 'water',
  [Zodiac.Scorpio]: 'water',
  [Zodiac.Pisces]: 'water',
};

export interface ElementVisual {
  element: ZodiacElement;
  // i18n keys resolved by the caller with t()
  labelKey: string;
  keywordKey: string;
  // lucide-react icon that symbolizes the element
  icon: LucideIcon;
  // Tailwind core utility classes (no compiler required)
  cardClass: string;
  titleClass: string;
  iconClass: string;
  valueClass: string;
  keywordClass: string;
}

const ELEMENT_VISUALS: Record<ZodiacElement, ElementVisual> = {
  fire: {
    element: 'fire',
    labelKey: 'element_fire',
    keywordKey: 'element_fire_keyword',
    icon: Flame,
    cardClass: 'border-orange-200 bg-gradient-to-br from-red-50 to-orange-50',
    titleClass: 'text-orange-700',
    iconClass: 'text-orange-500',
    valueClass: 'text-orange-700',
    keywordClass: 'text-orange-600',
  },
  earth: {
    element: 'earth',
    labelKey: 'element_earth',
    keywordKey: 'element_earth_keyword',
    icon: Mountain,
    cardClass: 'border-amber-200 bg-gradient-to-br from-amber-50 to-lime-50',
    titleClass: 'text-amber-800',
    iconClass: 'text-amber-700',
    valueClass: 'text-amber-800',
    keywordClass: 'text-amber-700',
  },
  air: {
    element: 'air',
    labelKey: 'element_air',
    keywordKey: 'element_air_keyword',
    icon: Wind,
    cardClass: 'border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50',
    titleClass: 'text-sky-700',
    iconClass: 'text-sky-500',
    valueClass: 'text-sky-700',
    keywordClass: 'text-sky-600',
  },
  water: {
    element: 'water',
    labelKey: 'element_water',
    keywordKey: 'element_water_keyword',
    icon: Droplet,
    cardClass: 'border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50',
    titleClass: 'text-blue-700',
    iconClass: 'text-blue-500',
    valueClass: 'text-blue-700',
    keywordClass: 'text-blue-600',
  },
};

export const getElementForSign = (sign: Zodiac | null | undefined): ZodiacElement | null =>
  sign ? SIGN_TO_ELEMENT[sign] ?? null : null;

export const getElementVisual = (sign: Zodiac | null | undefined): ElementVisual | null => {
  const element = getElementForSign(sign);
  return element ? ELEMENT_VISUALS[element] : null;
};
